import React, { createContext, ReactNode, useContext, useState } from "react";
import { DEMO_INVOICES, DEMO_TRANSACTIONS } from "../data/demo-data";
import { Invoice, Transaction } from "../types";

// Alert type for dashboard metrics
export interface Alert {
  id: string;
  type: "warning" | "info" | "critical";
  message: string;
  action?: string;
  actionLabel?: string;
  relatedInvoiceId?: string;
}

interface AppState {
  invoices: Invoice[];
  transactions: Transaction[];
  alerts: Alert[];
  lastSyncTime: string | null;
  isSyncing: boolean;
}

interface AppStateContextType {
  state: AppState;
  sendReminder: (invoiceId: string) => Promise<void>;
  chaseInvoice: (invoiceId: string, message?: string) => Promise<void>;
  updateTransaction: (
    transactionId: string,
    updates: Partial<Transaction>,
  ) => void;
  syncData: () => Promise<void>;
  dismissAlert: (alertId: string) => void;
}

const AppStateContext = createContext<AppStateContextType | undefined>(
  undefined,
);

export const AppStateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AppState>({
    invoices: DEMO_INVOICES,
    transactions: DEMO_TRANSACTIONS,
    alerts: [], // Alerts now generated dynamically from metrics service
    lastSyncTime: null,
    isSyncing: false,
  });

  const sendReminder = async (invoiceId: string): Promise<void> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setState((prevState) => {
      const updatedInvoices = prevState.invoices.map((invoice) =>
        invoice.id === invoiceId
          ? {
              ...invoice,
              reminderSent: true,
              lastReminderDate: new Date().toISOString(),
            }
          : invoice,
      );

      // Remove or update related alerts
      const updatedAlerts = prevState.alerts.filter(
        (alert) => alert.relatedInvoiceId !== invoiceId,
      );

      return {
        ...prevState,
        invoices: updatedInvoices,
        alerts: updatedAlerts,
      };
    });
  };

  const chaseInvoice = async (
    invoiceId: string,
    message?: string,
  ): Promise<void> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setState((prevState) => {
      const updatedInvoices = prevState.invoices.map((invoice) =>
        invoice.id === invoiceId
          ? {
              ...invoice,
              reminderSent: true,
              lastReminderDate: new Date().toISOString(),
            }
          : invoice,
      );

      // Remove or update related alerts
      const updatedAlerts = prevState.alerts.filter(
        (alert) => alert.relatedInvoiceId !== invoiceId,
      );

      return {
        ...prevState,
        invoices: updatedInvoices,
        alerts: updatedAlerts,
      };
    });
  };

  const updateTransaction = (
    transactionId: string,
    updates: Partial<Transaction>,
  ): void => {
    setState((prevState) => ({
      ...prevState,
      transactions: prevState.transactions.map((transaction) =>
        transaction.id === transactionId
          ? { ...transaction, ...updates }
          : transaction,
      ),
    }));
  };

  const syncData = async (): Promise<void> => {
    setState((prevState) => ({ ...prevState, isSyncing: true }));

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setState((prevState) => ({
      ...prevState,
      isSyncing: false,
      lastSyncTime: new Date().toISOString(),
    }));
  };

  const dismissAlert = (alertId: string): void => {
    setState((prevState) => ({
      ...prevState,
      alerts: prevState.alerts.filter((alert) => alert.id !== alertId),
    }));
  };

  return (
    <AppStateContext.Provider
      value={{
        state,
        sendReminder,
        chaseInvoice,
        updateTransaction,
        syncData,
        dismissAlert,
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = (): AppStateContextType => {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error("useAppState must be used within an AppStateProvider");
  }
  return context;
};
